/**
 * Organisation roles and what each may do. Everything in the console belongs to an organisation; a person's
 * rights come only from their role in it. The API enforces this matrix on every route; the web app reads the same
 * matrix to hide what a role cannot do.
 */
export const ROLES = ['owner', 'admin', 'billing', 'developer', 'viewer'] as const
export type Role = (typeof ROLES)[number]

export const ROLE_INFO: Record<Role, { label: string; description: string }> = {
  owner: { label: 'Owner', description: 'Everything, including deleting the organization and managing owners' },
  admin: { label: 'Admin', description: 'Members, keys, budgets, billing and settings' },
  billing: { label: 'Billing', description: 'Credits, payment methods, invoices and budgets; read-only elsewhere' },
  developer: { label: 'Developer', description: 'Create API keys and use the playground; read usage' },
  viewer: { label: 'Viewer', description: 'Read-only: usage, keys and balance' },
}

export const PERMISSIONS = [
  'usage.view', // usage pages, recent requests
  'keys.view', // list keys (never the secret)
  'keys.create', // create keys; revoke your own
  'keys.manage', // revoke any key, set key budgets
  'playground.run', // run requests (spends credits)
  'billing.view', // balance, payment history
  'billing.manage', // top-ups, payment methods, auto top-up, promo codes
  'budgets.manage', // organisation daily / monthly budgets
  'members.manage', // invite, change roles, remove
  'org.manage', // rename the organisation
  'org.delete',
] as const
export type Permission = (typeof PERMISSIONS)[number]

const MATRIX: Record<Role, Permission[]> = {
  owner: [...PERMISSIONS],
  admin: PERMISSIONS.filter((p) => p !== 'org.delete'),
  billing: ['usage.view', 'keys.view', 'billing.view', 'billing.manage', 'budgets.manage'],
  developer: ['usage.view', 'keys.view', 'keys.create', 'playground.run', 'billing.view'],
  viewer: ['usage.view', 'keys.view', 'billing.view'],
}

/** Older memberships created before roles existed carry `member`; they behave as developers. */
export function normalizeRole(role: string | null | undefined): Role {
  if (role && (ROLES as readonly string[]).includes(role)) return role as Role
  return 'developer'
}

export function can(role: string | null | undefined, permission: Permission): boolean {
  return MATRIX[normalizeRole(role)].includes(permission)
}

/** Roles a person may hand out: owners can make owners; admins can assign anything below owner. */
export function assignableRoles(byRole: string | null | undefined): Role[] {
  const r = normalizeRole(byRole)
  if (r === 'owner') return [...ROLES]
  if (r === 'admin') return ROLES.filter((x) => x !== 'owner')
  return []
}
